import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import { LeagueTeamResp } from 'types/team/leagueTeam'
import {
  AddTeamMemberPayload,
  CreateTeamPayload,
  GetAllTeamRespType,
  GetAllTeamsParams,
  GetTeamMembersParams,
  GetTeamMembersRespType,
  TeamMemberCandidate,
  TeamType,
} from 'types/team/team'

// get all teams of all organizations
const getAllTeams = async (params: GetAllTeamsParams) => {
  const response = await axiosGet(`v1/admins/teams`, params)
  return response.data
}
export const useGetAllTeams = (params: GetAllTeamsParams) => {
  const allTeams = useQuery<any>(
    ['get-all-teams-list'],
    () => getAllTeams(params),
    {
      enabled: false,
    },
  )
  return allTeams
}

// get all teams of one organization
const getAllTeamsOfOneOrganization = async (organizationId: string) => {
  const response = await axiosGet(`v1/organization-team/${organizationId}`)
  return response.data
}
export const useGetAllTeamsOfOneOrganization = (organizationId: string) => {
  const teamsOfOneOrg = useQuery<TeamType[]>(
    ['all-teams-by-organization-id'],
    () =>
      organizationId ? getAllTeamsOfOneOrganization(organizationId) : null,
    {
      enabled: false,
    },
  )
  return teamsOfOneOrg
}

// get all teams of one division
const getAllTeamsOfDivision = async (leagueId: string, divisionId: string) => {
  const response = await axiosGet(
    `v1/league-team/league/${leagueId}/division/${divisionId}`,
  )
  return response.data
}
export const useGetAllTeamsOfDivision = (
  leagueId: string,
  divisionId: string,
) => {
  const teamsOfOneDivision = useQuery<LeagueTeamResp[]>(
    ['all-teams-by-division-id'],
    () => getAllTeamsOfDivision(leagueId, divisionId),
    {
      enabled: false,
    },
  )
  return teamsOfOneDivision
}

// create team
const createTeamForOrganization = async ({
  organizationId,
  payload,
}: {
  organizationId: string
  payload: CreateTeamPayload
}) => {
  const response = await axiosPost(
    payload,
    `v1/organization-team/${organizationId}`,
  )
  return response.data
}

export const useCreateTeamForOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation(createTeamForOrganization, {
    onSuccess: newTeam => {
      queryClient.setQueryData(
        ['get-all-teams-list'],
        (oldData: GetAllTeamRespType | undefined) => {
          if (!oldData) return
          if (!oldData.count)
            return {
              ...oldData,
              count: oldData.count + 1,
              records: [newTeam],
            }
          return {
            ...oldData,
            count: oldData.count + 1,
            records: [newTeam, ...oldData.records],
          }
        },
      )
    },
  })
}

// update team
const updateTeamInOrganization = async ({
  teamId,
  payload,
}: {
  teamId: string
  payload: CreateTeamPayload
}) => {
  const response = await axiosPatch(payload, `v1/organization-team/${teamId}`)
  return response.data
}

export const useUpdateTeamInOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation(updateTeamInOrganization, {
    onSuccess: updatedTeam => {
      queryClient.setQueryData(
        ['get-all-teams-list'],
        (oldData: GetAllTeamRespType | undefined) => {
          if (!oldData) return undefined
          if (!oldData.count)
            return {
              ...oldData,
              records: [updatedTeam],
            }
          return {
            ...oldData,
            records: oldData.records.map(team =>
              team._id === updatedTeam._id ? updatedTeam : team,
            ),
          }
        },
      )
    },
  })
}

// get single team profile by id
const getTeamById = async (teamId: string) => {
  const response = await axiosGet(`v1/organization-team/profile/${teamId}`)
  return response.data
}
export const useGetTeamById = (teamId: string) => {
  return useQuery<TeamType>(['team-by-id', teamId], () => getTeamById(teamId), {
    enabled: false,
  })
}

// get all members of a team
const getTeamMembers = async (teamId: string, params: GetTeamMembersParams) => {
  const response = await axiosGet(
    `v1/organization-team-member/members/${teamId}`,
    params,
  )
  return response.data
}
export const useGetTeamMembers = (
  teamId: string,
  params: GetTeamMembersParams,
) => {
  return useQuery<GetTeamMembersRespType>(
    ['team-members', teamId],
    () => getTeamMembers(teamId, params),
    { enabled: false },
  )
}

// add a member/lead to a team
const addTeamMember = async ({
  teamId,
  payload,
}: {
  teamId: string
  payload: AddTeamMemberPayload
}) => {
  const response = await axiosPost(
    payload,
    `v1/organization-team-member/${teamId}`,
  )
  return response.data
}
export const useAddTeamMember = () => {
  const queryClient = useQueryClient()
  return useMutation(addTeamMember, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['team-members', variables.teamId])
    },
  })
}

// archive a member from a team (no DELETE endpoint — use status update)
const removeTeamMember = async ({
  memberId,
  teamId,
}: {
  memberId: string
  teamId: string
}) => {
  const response = await axiosPatch(
    { memberId, status: 'archived' },
    `v1/organization-team-member/status/${teamId}`,
  )
  return response.data
}
export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient()
  return useMutation(removeTeamMember, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['team-members', variables.teamId])
    },
  })
}

// change team status
const changeTeamStatus = async ({
  params,
  payload,
}: {
  params: string
  payload: string
}) => {
  const response = await axiosPatch(
    { status: payload },
    `v1/organization-team/${params}`,
  )
  return response.data
}
export const useChangeTeamStatus = () => {
  const queryClient = useQueryClient()
  return useMutation(changeTeamStatus, {
    onSuccess: updatedTeam => {
      queryClient.setQueryData(
        ['get-all-teams-list'],
        (oldData: GetAllTeamRespType | undefined) => {
          if (!oldData) return undefined
          return {
            ...oldData,
            records: oldData.records.map(team =>
              team._id === updatedTeam._id ? updatedTeam : team,
            ),
          }
        },
      )
    },
  })
}

// delete team
const deleteTeamInOrganization = async (teamId: string) => {
  const response = await axiosDelete(`v1/organization-team/${teamId}`)
  return response.data
}
export const useDeleteTeamInOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteTeamInOrganization, {
    onSuccess: deletedTeam => {
      queryClient.setQueryData(
        ['get-all-teams-list'],
        (oldData: GetAllTeamRespType | undefined) => {
          if (!oldData) return
          return {
            ...oldData,
            records: oldData.records.map(team =>
              team._id === deletedTeam._id ? deletedTeam : team,
            ),
          }
        },
      )
    },
  })
}

// search people who can be added as team members
const searchTeamMemberCandidates = async (query: string) => {
  const response = await axiosGet(`v1/organization-team-member/search`, {
    query,
  })
  return response.data
}
export const useSearchTeamMemberCandidates = (query: string, enabled = false) => {
  return useQuery<TeamMemberCandidate[]>(
    ['team-member-candidates', query],
    () => searchTeamMemberCandidates(query),
    { enabled },
  )
}
